<template>
  <ImpersonationBanner v-if="store.impersonation.active" />
  <router-view></router-view>
  <BackgroundTaskIndicator />
  <transition name="modal-fade">
    <div v-if="showPaymentModal" class="modal-background" @click.self="dismissWarning">
      <div class="modal-scroll">
        <PaymentWarningModal
          :status="store.billingAlert.status"
          :isAdmin="store.isAccountAdmin || store.isDashQuillAdmin"
          :isDashQuillAdmin="store.isDashQuillAdmin"
          :daysRemaining="daysRemaining"
          @closeModal="dismissWarning"
          @goToBilling="goToBilling"
        />
      </div>
    </div>
  </transition>
</template>

<script>
import { store, loadImpersonation, updateBillingAlert } from './store'
import * as coreApi from './api/core'
import { addEventListener as addIframeEventListener, removeEventListener as removeIframeEventListener } from './iframe'
import ImpersonationBanner from './components/shared/ImpersonationBanner.vue'
import BackgroundTaskIndicator from './components/shared/BackgroundTaskIndicator.vue'
import PaymentWarningModal from './modals/shared/PaymentWarningModal.vue'

export default {
  name: 'App',
  components: { ImpersonationBanner, BackgroundTaskIndicator, PaymentWarningModal },
  data() {
    return {
      store,
      paymentWarningDismissed: false
    }
  },
  computed: {
    elapsedDays() {
      const startDate = this.store.billingAlert.startDate
      if (!startDate) return 0
      const ms = Date.now() - new Date(startDate).getTime()
      return Math.max(0, Math.floor(ms / (1000 * 60 * 60 * 24)))
    },
    daysRemaining() {
      return Math.max(0, 20 - this.elapsedDays)
    },
    canSeePaymentModal() {
      return this.store.isAccountAdmin || this.store.isDashQuillAdmin || this.store.isDashQuillEmployee
    },
    showPaymentModal() {
      const status = this.store.billingAlert.status
      if (!status) return false
      if (!this.canSeePaymentModal) return false
      if (this.$route.path === '/settings/billing') return false
      if (status === 'locked') return this.store.isDashQuillAdmin ? !this.paymentWarningDismissed : true
      return !this.paymentWarningDismissed
    }
  },
  mounted() {
    loadImpersonation()
    this.fetchBillingAlert()
    this.setupHostNavigation()
  },
  beforeUnmount() {
    removeIframeEventListener('SESSION_NAVIGATE', this.handleHostNavigate)
  },
  methods: {
    // The extension asks us to change views in place rather than recreating the iframe,
    // so an in-progress recording survives switching to Faxes/Forms/etc. Recreating the
    // frame would also burn another one-time code and skip ScribeView.beforeRouteLeave,
    // which is what turns an in-SPA navigation into a stop-and-save.
    setupHostNavigation() {
      if (window.self === window.top) return
      addIframeEventListener('SESSION_NAVIGATE', this.handleHostNavigate)
    },

    handleHostNavigate(payload) {
      const path = payload && payload.path
      if (!path) return
      const query = (payload && payload.query) || {}
      this.$router.push({ path, query }).catch(() => {})
      window.parent.postMessage(
        { source: '__DASHQUILL_SESSION__', type: 'SESSION_NAVIGATED', payload: { path } },
        '*'
      )
    },
    async fetchBillingAlert() {
      try {
        const result = await coreApi.getAccountSettings()
        if (result && result.settings) {
          updateBillingAlert(result.settings)
          this.store.isAccountAdmin = result.isAccountAdmin
          this.store.isDashQuillAdmin = result.isDashQuillAdmin
          this.store.isDashQuillEmployee = result.isDashQuillEmployee || false
        }
      } catch {
        // Silently fail — user may not be logged in yet
      }
    },
    dismissWarning() {
      if (this.store.billingAlert.status === 'locked' && !this.store.isDashQuillAdmin) return
      this.paymentWarningDismissed = true
    },
    goToBilling() {
      this.paymentWarningDismissed = true
      this.$router.push('/settings/billing')
    }
  },
  watch: {
    '$route'() {
      if (this.elapsedDays >= 5) {
        this.paymentWarningDismissed = false
      }
    }
  }
}
</script>

<style>
@import '@/assets/theme.css';
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,100..1000;1,9..40,100..1000&display=swap');
@import url('https://fonts.googleapis.com/css2?family=Dancing+Script:wght@700&display=swap');
@import url('https://fonts.googleapis.com/css2?family=DM+Mono:wght@300;400;500&display=swap');

@font-face {
  font-family: 'Lucida Fax';
  src: url('@/assets/fonts/lucida-fax-regular.ttf');
}

:root {
  --color-link: #3a71fb;
}

.app {
  overflow-x: hidden;
}

html {
  /* Stops messages from getting wonky font sizes */
  -webkit-text-size-adjust: 100%;
  /* Safari and Chrome */
  -ms-text-size-adjust: 100%;
  /* IE/Edge */
  text-size-adjust: 100%;
  /* Standard */

  overflow-x: hidden;
  overscroll-behavior: none;
}

* {
  font-family: 'DM Sans', sans-serif;
  box-sizing: border-box;
  margin: 0;
}

#full {
  background: #FFF;
  width: 100%;
  height: 100dvh;
  overflow: hidden;
  font-size: 0.875rem;
}

/* Reserve the bottom nav's strip on phones.
   Selector shape matters: 31 of the 48 shell views re-declare
   `#full { height: 100vh }` in <style scoped>, which compiles to
   `#full[data-v-…]` — specificity (1,1,0). `html.has-app-nav #full` is (1,1,1)
   and so wins without needing !important.
   Gating on the class (set by AppSidebar while the nav is actually rendered)
   keeps the three patient-facing views that have no rail — AcceptInviteView,
   PatientSecureChatView, TelehealthSurveyView — and ScribeView mid-recording
   from reserving space for a bar that isn't there.
   #full is overflow:hidden with height:100% children, so shrinking it is all
   that's needed: every in-flow pane stops above the bar automatically. */
@media screen and (max-width: 700px) {
  html.has-app-nav #full {
    height: calc(100dvh - var(--app-mobile-nav-total));
  }
}

.title {
  margin-bottom: 5px;
}

.subtitle {
  line-height: 24px;
}

p {
  line-height: 24px;
}

/* Modals */

.modal-slide-enter-active,
.modal-slide-leave-active {
  transition: all ease-in-out 0.25s, left 0.01s, right 0.01s, top 0.01s, bottom 0.01s;
  transform: none;
  opacity: 1;
}

.modal-slide-enter-from {
  transform: translateX(10px);
  opacity: 0;
}

.modal-slide-leave-to {
  transform: translateX(-10px);
  opacity: 0;
}

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: all ease-in-out 0.25s;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

.modal-pop-enter-active,
.modal-pop-leave-active {
  transition: all ease-in-out 0.25s, left 0.01s, right 0.01s, top 0.01s, bottom 0.01s;
  transform: none;
  opacity: 1;
}

.modal-pop-enter-from,
.modal-pop-leave-to {
  opacity: 0;
  transform: scale(0.95);
}

.modal-appear-enter-active,
.modal-appear-leave-active {
  transition: all ease-in-out 0.25s, left 0.01s, right 0.01s, top 0.01s, bottom 0.01s;
  transform: none;
  opacity: 1;
}

.modal-appear-enter-from,
.modal-appear-leave-to {
  opacity: 0;
  transform: translateY(10px);
}

.dropdown-appear-enter-active,
.dropdown-appear-leave-active {
  transition: all ease-in-out 0.25s;
}

.dropdown-appear-enter-from,
.dropdown-appear-leave-to {
  opacity: 0;
  transform-origin: top;
  transform: translateY(-10px);
}

.loading {
  display: block;
  width: 48px;
  height: 24px;
  margin: auto;
}

.button.small .loading {
  height: 20px;
  width: 40px;
}

.error {
  color: #960030;
  display: block;
  margin: auto;
  margin-top: 10px;
  text-align: center;
}

.indicator-icon {
  display: inline-flex;
  width: 8px;
  height: 8px;
  background: #683fff;
  border-radius: 50%;
  color: #FFF;
  font-size: 0.5rem;
  font-weight: bold;
  justify-content: center;
  align-items: center;
}

.required {
  opacity: 0.5;
}

.vc-twitter-picker {
  width: 100% !important;
  box-shadow: none !important;
}

.vc-twitter-picker .body {
  padding: 0px !important;
  padding-top: 5px !important;
}

.vc-twitter-picker .swatch[aria-selected="true"] {
  border: 2px solid #00000030;
}

/* Global tooltip styles */
.v-popper__popper .v-popper__inner {
  font-size: 0.75rem !important;
  max-width: 400px !important;
  word-wrap: break-word;
}

/*
 * Hidden poppers keep position: absolute and their last computed translate3d on
 * <body>, which can park them thousands of px below the viewport and make the
 * whole page scrollable/jump (#full never overflows, so a stray popper is the
 * only thing that can). While hidden, pin them to the viewport — fixed boxes
 * never expand the page's scroll area — and park them off the right edge, never
 * below. !important is required to beat the library's inline styles.
 */
.v-popper__popper {
  overflow-anchor: none;
}

.v-popper__popper.v-popper__popper--hidden {
  position: fixed !important;
  transform: translate3d(100vw, 0, 0) !important;
}

.tooltip {
  font-size: 0.75rem !important;
  max-width: 400px !important;
}

.tooltip-inner {
  font-size: 0.75rem !important;
  max-width: 400px !important;
  word-wrap: break-word;
}
</style>
